import { __awaiter } from "tslib";
import { JSON_HEADERS, SearchForRetail } from '@/client/api';
import { buildCustomerHeaders } from '@/client/utils';
import { delay } from '@/utils/retry';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
// ---------------------------------------------------------------------------
// Path constants
// ---------------------------------------------------------------------------
const USER_PATH = '/user';
const IDENTITY_USER_PATH = '/identity/users';
const IDENTITY_ORGANIZATION_PATH = '/identity/organization';
// ---------------------------------------------------------------------------
// resaveUserPermissions tuning
// ---------------------------------------------------------------------------
/** Maximum poll-then-PUT attempts before giving up. */
const MAX_RESAVE_ATTEMPTS = 3;
/**
 * Per-attempt delay (ms) before reading the user from the backend.
 *
 * Mirrors the schedule used by the legacy `user.client.ts` (removed
 * in BS-1331) so the new endpoint reproduces backoff behaviour exactly.
 */
const RESAVE_DELAY_MS = [400, 800, 1500];
/** Tags invalidated by any mutation that touches a single CC user. */
const userMutationTags = (id) => [
    { type: CC_API_TAGS.UserList, id: 'LIST' },
    { type: CC_API_TAGS.User, id },
];
/** Tags invalidated by any mutation that touches a single Auth0 identity user. */
const identityUserMutationTags = (id) => [
    { type: CC_API_TAGS.IdentityUserList, id: 'LIST' },
    { type: CC_API_TAGS.IdentityUser, id },
];
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for User Management (CC user CRUD + Auth0 identity users).
 *
 * Extends the base `ccApi` with the 15 endpoints that previously lived in
 * the imperative `user.client.ts` module (removed in BS-1331). These
 * endpoints are designed to be consumed via the platform-services hooks in
 * `src/platform-services/users/` rather than imported directly.
 *
 * Two endpoints use a custom `queryFn`:
 *
 * 1. `resaveUserPermissions` — poll-then-PUT with backoff. Reads the user
 *    fresh from the backend until the expected area permission appears,
 *    then PUTs the permissions list back to force Auth0 propagation.
 * 2. `getOrganizationWebApp` — unauthenticated raw fetch (no Bearer
 *    header). Called pre-mount from the Auth0 provider wiring to bootstrap
 *    the SDK.
 *
 * @see src/platform-services/users (consumer hooks)
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const userManagementApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // CC user queries
        // -----------------------------------------------------------------------
        /**
         * Fetches a single CC user by ID.
         *
         * The backend returns the user wrapped in a single-element array; the
         * `transformResponse` here unwraps it so consumers receive a `User`.
         *
         * @endpoint GET /user/{id}
         */
        getUser: builder.query({
            query: ({ id }) => `${USER_PATH}/${id}`,
            transformResponse: (response) => (Array.isArray(response) ? response[0] : response),
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.User, id }],
        }),
        /**
         * Fetches the full list of CC users for the current tenant.
         *
         * @endpoint GET /user
         */
        getUserList: builder.query({
            query: () => USER_PATH,
            providesTags: [{ type: CC_API_TAGS.UserList, id: 'LIST' }],
        }),
        // -----------------------------------------------------------------------
        // CC user mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new CC user.
         *
         * @endpoint POST /user
         */
        createUser: builder.mutation({
            query: ({ data }) => ({
                url: USER_PATH,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: CC_API_TAGS.UserList, id: 'LIST' }],
        }),
        /**
         * Updates an existing CC user by ID.
         *
         * @endpoint PUT /user/{id}
         */
        updateUser: builder.mutation({
            query: ({ id, data }) => ({
                url: `${USER_PATH}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => userMutationTags(id),
        }),
        /**
         * Deletes a CC user by ID.
         *
         * @endpoint DELETE /user/{id}
         */
        deleteUser: builder.mutation({
            query: ({ id }) => ({
                url: `${USER_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => userMutationTags(id),
        }),
        // -----------------------------------------------------------------------
        // Auth0 identity user queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the full list of Auth0 identity users for the current tenant.
         *
         * @endpoint GET /identity/users
         */
        getIdentityUserList: builder.query({
            query: () => IDENTITY_USER_PATH,
            providesTags: [{ type: CC_API_TAGS.IdentityUserList, id: 'LIST' }],
        }),
        /**
         * Fetches a single Auth0 identity user by ID.
         *
         * Returns the richer `IdentityUserById` shape (includes `connection`
         * and `permissions`) used by the user-edit form.
         *
         * The backend may wrap the user in a single-element array (legacy
         * convention seen across other identity endpoints — see
         * `resaveUserPermissions` queryFn). The `transformResponse` here
         * unwraps it so consumers receive a single `IdentityUserById`.
         *
         * @endpoint GET /identity/users/{id}
         */
        getIdentityUserById: builder.query({
            query: ({ id }) => `${IDENTITY_USER_PATH}/${id}`,
            transformResponse: (response) => (Array.isArray(response) ? response[0] : response),
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.IdentityUser, id },
            ],
        }),
        /**
         * Fetches a user's Auth0 identity settings.
         *
         * Only `ADMIN` users may invoke this endpoint.
         *
         * @endpoint GET /identity/users/{id}/settings
         */
        getIdentityUserSettingsById: builder.query({
            query: ({ id }) => `${IDENTITY_USER_PATH}/${id}/settings`,
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.IdentityUserSettings, id },
            ],
        }),
        // -----------------------------------------------------------------------
        // Auth0 identity user mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new Auth0 identity user.
         *
         * The caller is expected to pre-format permissions (see
         * `formatPermissions` / `translateCreateIdentityUserRequestData` in
         * `src/search-for-retail/user/utils.ts`). The endpoint accepts the
         * raw backend payload shape — keeping translation at the
         * platform-services hook / caller level mirrors the existing consumer
         * pattern.
         *
         * @endpoint POST /identity/users
         */
        createIdentityUser: builder.mutation({
            query: ({ data }) => ({
                url: IDENTITY_USER_PATH,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: CC_API_TAGS.IdentityUserList, id: 'LIST' }],
        }),
        /**
         * Updates an existing Auth0 identity user.
         *
         * @endpoint PUT /identity/users/{id}
         */
        updateIdentityUser: builder.mutation({
            query: ({ id, data }) => ({
                url: `${IDENTITY_USER_PATH}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => identityUserMutationTags(id),
        }),
        /**
         * Deletes an Auth0 identity user by ID.
         *
         * Also invalidates the user's settings cache entry — Auth0 keeps
         * settings keyed by the (now-deleted) identity user.
         *
         * @endpoint DELETE /identity/users/{id}
         */
        deleteIdentityUser: builder.mutation({
            query: ({ id }) => ({
                url: `${IDENTITY_USER_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                ...identityUserMutationTags(id),
                { type: CC_API_TAGS.IdentityUserSettings, id },
            ],
        }),
        /**
         * Syncs the current user's profile with Auth0.
         *
         * Designed for `store.dispatch(api.endpoints.syncAuth0UserProfile.initiate())`
         * from the Auth0 provider — there is no React context dependency.
         *
         * @endpoint POST /identity/users/syncAuthUserProfile
         */
        syncAuth0UserProfile: builder.mutation({
            query: () => ({
                url: `${IDENTITY_USER_PATH}/syncAuthUserProfile`,
                method: 'POST',
            }),
            invalidatesTags: [CC_API_TAGS.UserProfile],
        }),
        /**
         * Re-saves a user's permissions by polling the backend until the
         * expected area permission appears, then PUTting the permissions
         * back to force Auth0 propagation.
         *
         * Uses a custom `queryFn` because the operation is a poll-then-PUT
         * loop with backoff that does not map onto a single HTTP request.
         * `baseQuery` is used internally so the standard auth/tenant headers
         * are injected automatically.
         *
         * Algorithm:
         * 1. For each attempt 0..2:
         *    a. wait `RESAVE_DELAY_MS[attempt]` ms
         *    b. GET the identity user (skip cache via the dispatched query)
         *    c. normalise the response (backend returns an array for a
         *       single user)
         *    d. if `permissions` is empty, return an error — refusing to PUT
         *    e. if any permission contains `|<expectedAreaName>`, PUT the
         *       (name, email, permissions) tuple back and return the result
         * 2. Otherwise return an error indicating the area was not found.
         *
         */
        resaveUserPermissions: builder.mutation({
            queryFn(_a, _api_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ userId, expectedAreaName }, _api, _extraOptions, baseQuery) {
                    for (let attempt = 0; attempt < MAX_RESAVE_ATTEMPTS; attempt += 1) {
                        // eslint-disable-next-line no-await-in-loop
                        yield delay(RESAVE_DELAY_MS[attempt]);
                        // eslint-disable-next-line no-await-in-loop
                        const readResult = yield baseQuery({
                            url: `${IDENTITY_USER_PATH}/${userId}`,
                            method: 'GET',
                        });
                        if (readResult.error) {
                            return { error: readResult.error };
                        }
                        const rawData = readResult.data;
                        const freshUser = Array.isArray(rawData) ? rawData[0] : rawData;
                        if (!(freshUser === null || freshUser === void 0 ? void 0 : freshUser.permissions) || freshUser.permissions.length === 0) {
                            const error = {
                                status: 'CUSTOM_ERROR',
                                error: 'Refusing to PUT — backend returned empty permissions',
                                data: { message: 'Refusing to PUT — backend returned empty permissions' },
                            };
                            return { error };
                        }
                        const hasNewArea = freshUser.permissions.some((permission) => permission.includes(`|${expectedAreaName}`));
                        if (hasNewArea) {
                            // eslint-disable-next-line no-await-in-loop
                            const writeResult = yield baseQuery({
                                url: `${IDENTITY_USER_PATH}/${userId}`,
                                method: 'PUT',
                                body: {
                                    name: freshUser.name,
                                    email: freshUser.email,
                                    permissions: [...freshUser.permissions],
                                },
                            });
                            if (writeResult.error) {
                                return { error: writeResult.error };
                            }
                            return { data: writeResult.data };
                        }
                    }
                    const error = {
                        status: 'CUSTOM_ERROR',
                        error: `Area "${expectedAreaName}" not found in permissions after ${MAX_RESAVE_ATTEMPTS} attempts`,
                        data: {
                            message: `Area "${expectedAreaName}" not found in permissions after ${MAX_RESAVE_ATTEMPTS} attempts`,
                        },
                    };
                    return { error };
                });
            },
            invalidatesTags: (_result, _error, { userId }) => identityUserMutationTags(userId),
        }),
        // -----------------------------------------------------------------------
        // Organization queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the list of Auth0 connections available for an organization.
         *
         * Used to populate the "Connection" dropdown in the user-create form.
         *
         * @endpoint GET /identity/organization/{customerId}/connections
         */
        getOrganizationConnectionsList: builder.query({
            query: ({ customerId }) => `${IDENTITY_ORGANIZATION_PATH}/${customerId}/connections`,
            providesTags: (_result, _error, { customerId }) => [
                { type: CC_API_TAGS.OrganizationConnections, id: customerId },
            ],
        }),
        /**
         * Fetches the Auth0 web-app configuration (clientId, domain, region)
         * for a tenant.
         *
         * Uses a custom `queryFn` with a raw `window.fetch` because this
         * endpoint is dispatched from the Auth0 provider wiring **before**
         * the SDK has a token to inject. The customer header is supplied via
         * `buildCustomerHeaders()`; no `Authorization` header is sent.
         *
         * Designed for `store.dispatch(api.endpoints.getOrganizationWebApp.initiate({ customerId }))`
         * — no React context dependency.
         */
        getOrganizationWebApp: builder.query({
            queryFn(_a, _b) {
                return __awaiter(this, arguments, void 0, function* ({ customerId }, { signal }) {
                    const url = `${SearchForRetail.API_URL}${IDENTITY_ORGANIZATION_PATH}/${customerId}/webApp`;
                    try {
                        const response = yield window.fetch(url, {
                            method: 'GET',
                            headers: Object.assign(Object.assign({}, buildCustomerHeaders()), JSON_HEADERS),
                            signal,
                        });
                        if (!response.ok) {
                            const errorData = yield response.json().catch(() => undefined);
                            const error = {
                                status: response.status,
                                data: errorData,
                            };
                            return { error };
                        }
                        const data = (yield response.json());
                        return { data };
                    }
                    catch (caught) {
                        const message = caught instanceof Error ? caught.message : String(caught);
                        const error = {
                            status: 'FETCH_ERROR',
                            error: message,
                        };
                        return { error };
                    }
                });
            },
            providesTags: (_result, _error, { customerId }) => [
                { type: CC_API_TAGS.OrganizationWebApp, id: customerId },
            ],
        }),
    }),
});
export const { useGetUserQuery, useGetUserListQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUserMutation, useGetIdentityUserListQuery, useGetIdentityUserByIdQuery, useGetIdentityUserSettingsByIdQuery, useCreateIdentityUserMutation, useUpdateIdentityUserMutation, useDeleteIdentityUserMutation, useSyncAuth0UserProfileMutation, useResaveUserPermissionsMutation, useGetOrganizationConnectionsListQuery, useGetOrganizationWebAppQuery, } = userManagementApi;
